import React, { createContext, useContext, useState, useCallback, useMemo, useRef, useEffect } from 'react';

const ToastContext = createContext({ success: () => {}, error: () => {} });
export const useToast = () => useContext(ToastContext);

const DURATION = { success: 4000, error: 6000 };
const ICON = {
  success: 'M5 13l4 4L19 7',
  error: 'M12 8v5M12 16.5v.01',
};

/**
 * App-wide toasts: `const toast = useToast(); toast.success('Category added');`
 * Top-right on desktop, bottom of the screen on phones. Auto-dismiss (errors stay longer), pause
 * while hovered, dismissible, announced to screen readers via a polite live region.
 */
export function ToastProvider({ children }) {
  const [items, setItems] = useState([]);
  const timers = useRef(new Map());
  const nextId = useRef(1);

  const dismiss = useCallback((id) => {
    clearTimeout(timers.current.get(id));
    timers.current.delete(id);
    setItems((list) => list.filter((t) => t.id !== id));
  }, []);

  const arm = useCallback((id, tone) => {
    clearTimeout(timers.current.get(id));
    timers.current.set(id, setTimeout(() => dismiss(id), DURATION[tone]));
  }, [dismiss]);

  const push = useCallback((tone, message) => {
    const id = nextId.current++;
    setItems((list) => [...list.slice(-3), { id, tone, message }]); // keep at most 4 on screen
    arm(id, tone);
  }, [arm]);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const api = useMemo(() => ({ success: (m) => push('success', m), error: (m) => push('error', m) }), [push]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="adm-toasts" role="status" aria-live="polite">
        {items.map((t) => (
          <div
            key={t.id}
            className={`adm-toast adm-toast-${t.tone}`}
            onMouseEnter={() => clearTimeout(timers.current.get(t.id))}
            onMouseLeave={() => arm(t.id, t.tone)}
          >
            <span className="adm-toast-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24"><path d={ICON[t.tone]} /></svg>
            </span>
            <span className="adm-toast-msg">{t.message}</span>
            <button type="button" className="adm-toast-x" onClick={() => dismiss(t.id)} aria-label="Dismiss">×</button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
